import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useMessageModal } from "../admin/contexts/MessageModalContext";
import { useFormat } from "../context/FormatContext";
import { useCurrencyConverter } from "../context/CurrencyConverterContext";
import { useBubbleModal, type BubbleStep } from "../context/BubbleModalContext";
import DataTable, { type Column } from "../admin/components/DataTable";
import "../styles/marketPriceAnalysis.css";

type RegionOption = {
  id: number;
  region_name: string;
  region_code?: string | null;
  country_code?: string | null;
  dataforseo_location_name?: string | null;
  dataforseo_location_code?: number | null;
  dataforseo_language_code?: string | null;
  currency_code?: string | null;
};

type MarketResult = {
  id?: number;
  title?: string;
  sellerName?: string;
  seller_name?: string;
  domain?: string;
  source?: string;
  url?: string;
  priceValue?: number;
  price_value?: number;
  originalPriceValue?: number | null;
  original_price_value?: number | null;
  originalCurrency?: string | null;
  original_currency?: string | null;
  conversionRate?: number | null;
  conversion_rate?: number | null;
  currency?: string;
  currencyCode?: string;
  rating?: number | null;
  created_at?: string | null;
  completed_at?: string | null;
  reviewsCount?: number | null;
  reviews_count?: number | null;
  rankPosition?: number | null;
  rank_position?: number | null;
  matchScore?: number;
  match_score?: number;
  matchType?: "Exact" | "Similar" | "Weak" | "Unmatched";
  match_type?: "Exact" | "Similar" | "Weak" | "Unmatched";
};

type NormalizedMarketResult = {
  id?: number;
  title: string;
  sellerName: string;
  domain: string;
  source: string;
  url: string;
  priceValue: number | null;
  originalPriceValue?: number | null;
  original_price_value?: number | null;
  originalCurrency?: string | null;
  original_currency?: string | null;
  conversionRate?: number | null;
  conversion_rate?: number | null;
  currency: string;
  currencyCode?: string;
  rating: number | null;
  reviewsCount: number | null;
  rankPosition: number | null;
  matchScore: number;
  matchType: "Exact" | "Similar" | "Weak" | "Unmatched";
};

type MarketAnalysisResponse = {
  runId: number;
  created_at?: string | null;
  completed_at?: string | null;
  searchKeyword: string;
  locationName?: string;
  currencyCode?: string;
  lowestPrice: number | null;
  averagePrice: number | null;
  highestPrice: number | null;
  resultCount: number;
  accessType: "Free" | "Paid";
  chargedAmount: number;
  wallet?: {
    balanceAmount: number;
    freeSearchUsed: boolean;
  };
  marketTrend?: {
    rangeDays: number;
    points: MarketTrendPoint[];
  };
  aiSuggestion?: MarketAiSuggestion;
  results: MarketResult[];
};

type WalletTransaction = {
  id: number;
  transaction_type: "Credit" | "Debit" | "FreeSearch";
  amount: number;
  reference_type?: string | null;
  reference_id?: string | null;
  note?: string | null;
  created_at: string;

  search_keyword?: string | null;
  access_type?: "Free" | "Paid" | null;
  analysis_status?: string | null;
  result_count?: number | null;
};

type WalletTopup = {
  id: number;
  payment_ref: string;
  plan_id: number;
  amount: number;
  payment_currency?: string | null;
  title?: string | null;
  description?: string | null;
  payment_method?: string | null;
  payment_provider?: "SecurePay" | "Stripe" | string | null;
  status: "Pending" | "Success" | "Failed" | "Cancelled";
  securepay_transaction_id?: string | null;
  stripe_payment_intent_id?: string | null;
  wallet_credited: number;
  source_type?: string | null;
  website_offer_id?: number | null;
  display_ref?: string | null;
  display_method?: string | null;
  created_at: string;
  updated_at: string;
};

type WalletHistoryData = {
  wallet: {
    balanceAmount: number;
    freeSearchUsed: boolean;
  };
  transactions: WalletTransaction[];
  topups: WalletTopup[];
};

type MarketTrendPoint = {
  date_label: string;
  lowest_price: number | null;
  average_price: number | null;
  highest_price: number | null;
  total_runs: number;
};

type MarketAiSuggestion = {
  trendDirection: "up" | "down" | "stable" | "insufficient";
  changePercent: number;
  seller: {
    suggestedPrice: number | null;
    message: string;
  };
  buyer: {
    targetPrice: number | null;
    message: string;
  };
  marketRange?: {
    lowest: number;
    average: number;
    highest: number;
  };
};

type SearchHistoryItem = {
  runId: number;
  searchKeyword: string;
  locationName: string;
  countryCode?: string | null;
  currencyCode?: string | null;
  accessType?: string | null;
  chargedAmount: number;
  lowestPrice: number;
  averagePrice: number;
  highestPrice: number;
  resultCount: number;
  created_at: string;
  completed_at?: string | null;
};

const API_BASE_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000";

const getStoredSubscriberUser = () => {
  try {
    const storedSubscriberRaw = localStorage.getItem("subscriber_user");

    if (!storedSubscriberRaw) return null;

    const subscriberUser = JSON.parse(storedSubscriberRaw);

    if (!subscriberUser?.id) return null;

    if (
      subscriberUser?.tokenExpiry &&
      Number(subscriberUser.tokenExpiry) < Date.now()
    ) {
      localStorage.removeItem("subscriber_user");
      return null;
    }

    return subscriberUser;
  } catch (error) {
    console.error("Invalid subscriber_user in localStorage:", error);
    localStorage.removeItem("subscriber_user");
    return null;
  }
};

const VISITOR_COUNTRY_CACHE_KEY = "lyrazone_visitor_country_by_ip";

type VisitorCountryInfo = {
  countryName: string;
  countryCode: string;
};

const normalizeCountryText = (value = "") => {
  return String(value || "")
    .trim()
    .toLowerCase();
};

const getCachedVisitorCountry = (): VisitorCountryInfo | null => {
  try {
    const cachedRaw = sessionStorage.getItem(VISITOR_COUNTRY_CACHE_KEY);

    if (!cachedRaw) return null;

    const cached = JSON.parse(cachedRaw);

    if (
      !cached?.createdAt ||
      Date.now() - cached.createdAt > 1000 * 60 * 60 * 6
    ) {
      sessionStorage.removeItem(VISITOR_COUNTRY_CACHE_KEY);
      return null;
    }

    return {
      countryName: cached.countryName || "",
      countryCode: cached.countryCode || "",
    };
  } catch {
    sessionStorage.removeItem(VISITOR_COUNTRY_CACHE_KEY);
    return null;
  }
};

const setCachedVisitorCountry = (country: VisitorCountryInfo) => {
  sessionStorage.setItem(
    VISITOR_COUNTRY_CACHE_KEY,
    JSON.stringify({
      ...country,
      createdAt: Date.now(),
    }),
  );
};

const fetchVisitorCountryByIp =
  async (): Promise<VisitorCountryInfo | null> => {
    const cachedCountry = getCachedVisitorCountry();

    if (cachedCountry?.countryName || cachedCountry?.countryCode) {
      return cachedCountry;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/visitor-country`, {
        withCredentials: true,
      });

      const data = response.data?.data || {};

      const visitorCountry = {
        countryName: String(data.countryName || "").trim(),
        countryCode: String(data.countryCode || "")
          .trim()
          .toUpperCase(),
      };

      if (visitorCountry.countryName || visitorCountry.countryCode) {
        setCachedVisitorCountry(visitorCountry);
        return visitorCountry;
      }
    } catch (error) {
      console.error("Visitor country detection error:", error);
    }

    return null;
  };

const findMatchingRegion = ({
  regions,
  countryName,
  countryCode,
}: {
  regions: RegionOption[];
  countryName?: string;
  countryCode?: string;
}) => {
  const normalizedCountryName = normalizeCountryText(countryName);
  const normalizedCountryCode = normalizeCountryText(countryCode);

  return regions.find((region) => {
    const regionName = normalizeCountryText(region.region_name);
    const regionCode = normalizeCountryText(region.region_code || "");
    const regionCountryCode = normalizeCountryText(region.country_code || "");

    return (
      (!!normalizedCountryName && regionName === normalizedCountryName) ||
      (!!normalizedCountryCode &&
        regionCountryCode === normalizedCountryCode) ||
      (!!normalizedCountryCode && regionCode === normalizedCountryCode)
    );
  });
};

const MarketPriceTrendChart = ({
  points,
  formatMarketResultCurrency,
}: {
  points: MarketTrendPoint[];
  formatMarketResultCurrency: (value?: number | null) => string;
}) => {
  const validPoints = points.filter(
    (item) => Number(item.average_price || 0) > 0,
  );

  if (!validPoints.length) {
    return (
      <div className="market-trend-empty">
        No historical price fluctuation data found yet.
      </div>
    );
  }

  const values = validPoints.map((item) => Number(item.average_price || 0));
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue || 1;

  // Dynamic width prevents 30-day labels from overlapping
  const width = Math.max(760, validPoints.length * 58);
  const height = 290;

  const paddingX = 44;
  const paddingTop = 34;
  const paddingBottom = 56;

  const chartWidth = width - paddingX * 2;
  const chartHeight = height - paddingTop - paddingBottom;

  const getX = (index: number) => {
    if (validPoints.length === 1) return width / 2;

    return paddingX + (index / (validPoints.length - 1)) * chartWidth;
  };

  const getY = (value: number) => {
    return paddingTop + ((maxValue - value) / range) * chartHeight;
  };

  const getDateParts = (dateValue: string) => {
    const date = new Date(dateValue);

    const day = date.toLocaleDateString("en-MY", {
      day: "2-digit",
    });

    const month = date.toLocaleDateString("en-MY", {
      month: "short",
    });

    return {
      day,
      month,
    };
  };

  const linePoints = validPoints
    .map((item, index) => {
      const x = getX(index);
      const y = getY(Number(item.average_price || 0));

      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div className="market-trend-chart-wrap">
      <div className="market-trend-chart-meta">
        <span>Lowest: {formatMarketResultCurrency(minValue)}</span>
        <span>Highest: {formatMarketResultCurrency(maxValue)}</span>
      </div>

      <svg viewBox={`0 0 ${width} ${height}`} className="market-trend-chart">
        {validPoints.map((item, index) => {
          const value = Number(item.average_price || 0);
          const x = getX(index);
          const y = getY(value);
          const barHeight = height - paddingBottom - y;
          const dateParts = getDateParts(item.date_label);

          return (
            <g key={`${item.date_label}-${index}`}>
              <rect
                x={x - 10}
                y={y}
                width="20"
                height={barHeight}
                rx="6"
                className="market-trend-bar"
              />

              <circle cx={x} cy={y} r="5" className="market-trend-dot" />

              <text
                x={x}
                y={height - 34}
                textAnchor="middle"
                className="market-trend-date-label"
              >
                <tspan x={x}>{dateParts.day}</tspan>
                <tspan x={x} dy="13">
                  {dateParts.month}
                </tspan>
              </text>

              <title>{`${item.date_label}: ${formatMarketResultCurrency(value)}`}</title>
            </g>
          );
        })}

        <polyline points={linePoints} className="market-trend-line" />
      </svg>
    </div>
  );
};

const MarketPriceAnalysis: React.FC = () => {
  const navigate = useNavigate();

  const { showModal } = useMessageModal();
  const { showBubble } = useBubbleModal();
  const { formatDateToDMY } = useFormat();
  const { formatLocalCurrencyOnly } = useCurrencyConverter();

  const [storedUser, setStoredUser] = useState<any>(() =>
    getStoredSubscriberUser(),
  );

  const exportRef = useRef<HTMLDivElement | null>(null);

  const LAST_SEARCH_STORAGE_KEY = "lyrazone_market_analysis_last_result";

  const [regions, setRegions] = useState<RegionOption[]>([]);
  const [countrySearch, setCountrySearch] = useState(
    storedUser?.country || "Malaysia",
  );
  const [selectedCountryName, setSelectedCountryName] = useState(
    storedUser?.country || "Malaysia",
  );
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  const countryDropdownRef = useRef<HTMLDivElement | null>(null);
  const pdfReportRef = useRef<HTMLDivElement | null>(null);

  const [searchKeyword, setSearchKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<MarketAnalysisResponse | null>(null);
  const [error, setError] = useState("");
  const [accessMessage, setAccessMessage] = useState("");
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [walletActiveTab, setWalletActiveTab] = useState<
    "transactions" | "topups"
  >("transactions");
  const [walletHistory, setWalletHistory] = useState<WalletHistoryData | null>(
    null,
  );
  const [walletHistoryLoading, setWalletHistoryLoading] = useState(false);
  const [walletHistoryError, setWalletHistoryError] = useState("");
  const [trendRange, setTrendRange] = useState<30 | 90>(30);
  const [marketTrendPoints, setMarketTrendPoints] = useState<
    MarketTrendPoint[]
  >([]);
  const [aiSuggestion, setAiSuggestion] = useState<MarketAiSuggestion | null>(
    null,
  );
  const [trendLoading, setTrendLoading] = useState(false);
  const SEARCH_HISTORY_LIMIT = 10;

  const [showSearchHistoryModal, setShowSearchHistoryModal] = useState(false);
  const [searchHistoryRows, setSearchHistoryRows] = useState<
    SearchHistoryItem[]
  >([]);
  const [searchHistoryTotal, setSearchHistoryTotal] = useState(0);
  const [searchHistoryHasMore, setSearchHistoryHasMore] = useState(false);
  const [searchHistoryLoading, setSearchHistoryLoading] = useState(false);

  const [historySearch, setHistorySearch] = useState("");
  const [historyDateFrom, setHistoryDateFrom] = useState("");
  const [historyDateTo, setHistoryDateTo] = useState("");

  const [historyExportRows, setHistoryExportRows] = useState<
    SearchHistoryItem[]
  >([]);

  const historyExportRef = useRef<HTMLDivElement | null>(null);
  const MARKET_TOUR_PROMPT_KEY = "lyrazone_market_analysis_tour_prompt_seen";

  const startMarketAnalysisTour = () => {
    const steps: BubbleStep[] = [];

    const isLoggedIn = Boolean(storedUser);

    const currentWalletBalance = Number(
      walletBalance || analysis?.wallet?.balanceAmount || 0,
    );

    // Case 1: Not logged in / logged out
    if (!isLoggedIn) {
      window.dispatchEvent(new CustomEvent("openSubscriberMenuForTour"));

      steps.push({
        selector:
          '[data-tour="subscriber-login"], [data-tour="subscriber-account-menu"]',
        text: "Login here or Signup if haven't yet and come back here.",
        placement: "bottom",
      });
    }

    // Case 2: Logged in but wallet balance is zero
    if (isLoggedIn && currentWalletBalance <= 0) {
      steps.push({
        selector: '[data-tour="top-up-balance"]',
        title: "Top Up Balance",
        text: "Add funds here.",
        placement: "bottom",
      });
    }

    // Case 3: Logged in and wallet available
    // Also included after Top Up step for zero-balance users.
    if (isLoggedIn) {
      steps.push({
        selector: '[data-tour="wallet-badge"]',
        title: "Wallet Balance",
        text: "Get Transactions and Top-Up details.",
        placement: "left",
      });
    }

    // Common steps for all users
    steps.push(
      {
        selector: '[data-tour="market-country"]',
        title: "Select Market Country",
        text: "Select Market wherein you want search.",
        placement: "bottom",
      },
      {
        selector: '[data-tour="search-keyword"]',
        title: "Search Product Keyword",
        text: "Type Product Name, Model and Brand.",
        placement: "bottom",
      },
      {
        selector: '[data-tour="analyze-market"]',
        title: "Analyze Market Price",
        text: "Analysis result based on Google Online Market will show here.",
        placement: "bottom",
      },
    );

    // Logged-in users only
    if (isLoggedIn) {
      steps.push({
        selector: '[data-tour="last-search"]',
        title: "Get Your Last Search",
        text: "Get your last search, no matter whenever you did.",
        placement: "bottom",
      });
    }

    setTimeout(() => {
      showBubble(steps);
    }, 350);
  };

  const shouldShowTourGuidePrompt = async () => {
    if (!storedUser) return true;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/market-analysis/wallet/history`,
        {
          withCredentials: true,
        },
      );

      const transactions = response.data?.data?.transactions || [];

      return transactions.length <= 1;
    } catch (error) {
      console.error("Tour guide checkpoint error:", error);
      return false;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const runTourCheckpoint = async () => {
      const alreadyPrompted = sessionStorage.getItem(MARKET_TOUR_PROMPT_KEY);

      if (alreadyPrompted) return;

      const canShowTourPrompt = await shouldShowTourGuidePrompt();

      if (!isMounted || !canShowTourPrompt) return;

      sessionStorage.setItem(MARKET_TOUR_PROMPT_KEY, "1");

      showModal({
        type: "confirm",
        title: "Tour Guide",
        message: "Would you like to have a tour guide of this page?",
        confirmText: "YES",
        cancelText: "NO",
        onConfirm: startMarketAnalysisTour,
      });
    };

    runTourCheckpoint();

    return () => {
      isMounted = false;
    };
  }, [storedUser?.id]);

  useEffect(() => {
    try {
      const savedRaw = localStorage.getItem(LAST_SEARCH_STORAGE_KEY);

      if (!savedRaw) return;

      const saved = JSON.parse(savedRaw);

      if (saved?.analysis) {
        setAnalysis(saved.analysis);
        setMarketTrendPoints(saved.marketTrendPoints || []);
        setAiSuggestion(saved.aiSuggestion || null);

        if (saved.analysis?.wallet) {
          setWalletBalance(Number(saved.analysis.wallet.balanceAmount || 0));
        }

        if (saved.analysis?.searchKeyword) {
          setSearchKeyword(saved.analysis.searchKeyword);
        }

        if (saved.analysis?.locationName) {
          setCountrySearch(saved.analysis.locationName);
          setSelectedCountryName(saved.analysis.locationName);
        }
      }
    } catch (error) {
      console.error("Restore last Market Analysis result error:", error);
    }
  }, []);

  useEffect(() => {
    const refreshStoredUser = () => {
      const latestUser = getStoredSubscriberUser();

      setStoredUser(latestUser);

      if (!latestUser) {
        setWalletBalance(null);
        setWalletHistory(null);
        setWalletModalOpen(false);
      }
    };

    refreshStoredUser();

    window.addEventListener("focus", refreshStoredUser);
    window.addEventListener("storage", refreshStoredUser);
    window.addEventListener("subscriber-auth-changed", refreshStoredUser);

    return () => {
      window.removeEventListener("focus", refreshStoredUser);
      window.removeEventListener("storage", refreshStoredUser);
      window.removeEventListener("subscriber-auth-changed", refreshStoredUser);
    };
  }, []);

  useEffect(() => {
    fetchSubscriberWallet();

    const refreshWalletOnFocus = () => {
      fetchSubscriberWallet();
    };

    window.addEventListener("focus", refreshWalletOnFocus);

    return () => {
      window.removeEventListener("focus", refreshWalletOnFocus);
    };
  }, []);

  // useEffect(() => {
  //   let isMounted = true;

  //   const fetchRegions = async () => {
  //     try {
  //       const response = await axios.get(`${API_BASE_URL}/regions`, {
  //         withCredentials: true,
  //       });

  //       if (!isMounted || !response.data?.success) return;

  //       const regionList: RegionOption[] = response.data.data || [];
  //       setRegions(regionList);

  //       const defaultCountry = storedUser?.country || "Malaysia";

  //       const matchedRegion = regionList.find(
  //         (region) =>
  //           String(region.region_name || "").toLowerCase() ===
  //             String(defaultCountry || "").toLowerCase() ||
  //           String(region.country_code || "").toLowerCase() ===
  //             String(defaultCountry || "").toLowerCase(),
  //       );

  //       if (matchedRegion) {
  //         setCountrySearch(matchedRegion.region_name);
  //         setSelectedCountryName(matchedRegion.region_name);
  //         setSelectedCountryCode(matchedRegion.country_code || "");
  //       } else {
  //         setCountrySearch(defaultCountry);
  //         setSelectedCountryName(defaultCountry);
  //         setSelectedCountryCode("");
  //       }
  //     } catch (error) {
  //       console.error("Fetch regions error:", error);
  //     }
  //   };

  //   fetchRegions();

  //   return () => {
  //     isMounted = false;
  //   };
  // }, [storedUser?.country]);

  useEffect(() => {
    let isMounted = true;

    const fetchRegions = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/regions`, {
          withCredentials: true,
        });

        if (!isMounted || !response.data?.success) return;

        const regionList: RegionOption[] = response.data.data || [];

        setRegions(regionList);

        let defaultCountryName = storedUser?.country || "";
        let defaultCountryCode = "";

        if (!defaultCountryName) {
          const visitorCountry = await fetchVisitorCountryByIp();

          if (!isMounted) return;

          defaultCountryName = visitorCountry?.countryName || "";
          defaultCountryCode = visitorCountry?.countryCode || "";
        }

        const matchedRegion =
          findMatchingRegion({
            regions: regionList,
            countryName: defaultCountryName,
            countryCode: defaultCountryCode,
          }) ||
          findMatchingRegion({
            regions: regionList,
            countryName: "Malaysia",
            countryCode: "MY",
          });

        if (matchedRegion) {
          setCountrySearch(matchedRegion.region_name);
          setSelectedCountryName(matchedRegion.region_name);
          setSelectedCountryCode(matchedRegion.country_code || "");
        } else {
          const fallbackCountry = defaultCountryName || "Malaysia";

          setCountrySearch(fallbackCountry);
          setSelectedCountryName(fallbackCountry);
          setSelectedCountryCode(defaultCountryCode || "");
        }
      } catch (error) {
        console.error("Fetch regions error:", error);
      }
    };

    fetchRegions();

    return () => {
      isMounted = false;
    };
  }, [storedUser?.country]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    document.body.classList.add("market-analysis-page-active");

    return () => {
      document.body.classList.remove("market-analysis-page-active");
    };
  }, []);

  const fetchSearchHistory = async ({ reset = true } = {}) => {
    try {
      setSearchHistoryLoading(true);

      const offset = reset ? 0 : searchHistoryRows.length;

      const response = await axios.get(
        `${API_BASE_URL}/market-analysis/search-history`,
        {
          withCredentials: true,
          params: {
            limit: SEARCH_HISTORY_LIMIT,
            offset,
            search: historySearch.trim() || undefined,
            dateFrom: historyDateFrom || undefined,
            dateTo: historyDateTo || undefined,
          },
        },
      );

      const data = response.data?.data;

      if (response.data?.success) {
        setSearchHistoryRows((prev) =>
          reset ? data.rows || [] : [...prev, ...(data.rows || [])],
        );

        setSearchHistoryTotal(Number(data.total || 0));
        setSearchHistoryHasMore(Boolean(data.hasMore));
      }
    } catch (error: any) {
      setError(
        error?.response?.data?.message ||
          error.message ||
          "Failed to fetch search history.",
      );
    } finally {
      setSearchHistoryLoading(false);
    }
  };

  const handleOpenSearchHistory = async () => {
    setShowSearchHistoryModal(true);
    await fetchSearchHistory({ reset: true });
  };

  const handleShowMoreSearchHistory = async () => {
    await fetchSearchHistory({ reset: false });
  };

  const handleShowLessSearchHistory = async () => {
    await fetchSearchHistory({ reset: true });
  };

  const handleClearHistoryFilters = async () => {
    setHistorySearch("");
    setHistoryDateFrom("");
    setHistoryDateTo("");

    setTimeout(() => {
      fetchSearchHistory({ reset: true });
    }, 0);
  };

  const handleLoadSearchHistoryRecord = async (runId: number) => {
    try {
      setLoading(true);
      setError("");
      setAccessMessage("");

      const response = await axios.get(
        `${API_BASE_URL}/market-analysis/search-history/${runId}`,
        {
          withCredentials: true,
        },
      );

      if (!response.data?.success) {
        setError(response.data?.message || "Unable to load selected search.");
        return;
      }

      const historyData = response.data.data;

      setAnalysis(historyData);
      setMarketTrendPoints(historyData?.marketTrend?.points || []);
      setAiSuggestion(historyData?.aiSuggestion || null);

      if (historyData?.wallet) {
        setWalletBalance(Number(historyData.wallet.balanceAmount || 0));
      }

      if (historyData?.searchKeyword) {
        setSearchKeyword(historyData.searchKeyword);
      }

      if (historyData?.locationName) {
        setCountrySearch(historyData.locationName);
        setSelectedCountryName(historyData.locationName);
        setSelectedCountryCode(historyData.countryCode || "");
      }

      localStorage.setItem(
        LAST_SEARCH_STORAGE_KEY,
        JSON.stringify({
          analysis: historyData,
          marketTrendPoints: historyData?.marketTrend?.points || [],
          aiSuggestion: historyData?.aiSuggestion || null,
          savedAt: new Date().toISOString(),
        }),
      );

      setShowSearchHistoryModal(false);
    } catch (error: any) {
      setError(
        error?.response?.data?.message ||
          error.message ||
          "Failed to load selected search.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExportSearchHistoryPdf = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/market-analysis/search-history`,
        {
          withCredentials: true,
          params: {
            exportAll: 1,
            search: historySearch.trim() || undefined,
            dateFrom: historyDateFrom || undefined,
            dateTo: historyDateTo || undefined,
          },
        },
      );

      const rows = response.data?.data?.rows || [];

      if (!rows.length) {
        setError("No search history available to export.");
        return;
      }

      setHistoryExportRows(rows);

      await new Promise((resolve) => setTimeout(resolve, 120));

      if (!historyExportRef.current) {
        setError("Unable to prepare search history export.");
        return;
      }

      const html2canvas = (await import("html2canvas")).default;
      const jsPDFModule = await import("jspdf");
      const jsPDF = jsPDFModule.default;

      const canvas = await html2canvas(historyExportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#f8f6f0",
        windowWidth: 1200,
      });

      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const margin = 8;
      const headerHeight = 10;
      const footerHeight = 9;
      const contentX = margin;
      const contentY = margin + headerHeight;
      const contentWidth = pageWidth - margin * 2;
      const contentHeight =
        pageHeight - margin * 2 - headerHeight - footerHeight;

      const pxPerMm = canvas.width / contentWidth;
      const sliceHeightPx = Math.floor(contentHeight * pxPerMm);

      let sourceY = 0;
      let pageNo = 1;

      while (sourceY < canvas.height) {
        if (pageNo > 1) pdf.addPage();

        const currentSliceHeight = Math.min(
          sliceHeightPx,
          canvas.height - sourceY,
        );

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = currentSliceHeight;

        const ctx = pageCanvas.getContext("2d");

        if (ctx) {
          ctx.fillStyle = "#f8f6f0";
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

          ctx.drawImage(
            canvas,
            0,
            sourceY,
            canvas.width,
            currentSliceHeight,
            0,
            0,
            canvas.width,
            currentSliceHeight,
          );
        }

        const pageImgData = pageCanvas.toDataURL("image/png");
        const pageImgHeight = currentSliceHeight / pxPerMm;

        pdf.addImage(
          pageImgData,
          "PNG",
          contentX,
          contentY,
          contentWidth,
          pageImgHeight,
        );

        sourceY += currentSliceHeight;
        pageNo += 1;
      }

      const totalPages = pdf.getNumberOfPages();
      const generatedAt = getReportDateTime();
      const REPORT_URL = "https://lyrazone.com.my/market-price-analysis";

      for (let page = 1; page <= totalPages; page += 1) {
        pdf.setPage(page);

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8.5);
        pdf.setTextColor(25, 25, 25);
        pdf.text(
          "LyraZone Solutions | Market Search History Report",
          margin,
          7,
        );

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        pdf.setTextColor(90, 90, 90);
        pdf.text(`Generated: ${generatedAt}`, margin, 11);
        pdf.text(REPORT_URL, pageWidth - margin, 11, { align: "right" });

        pdf.setDrawColor(209, 157, 40);
        pdf.setLineWidth(0.2);
        pdf.line(margin, 14, pageWidth - margin, 14);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        pdf.setTextColor(90, 90, 90);
        pdf.text(REPORT_URL, margin, pageHeight - 5);
        pdf.text(`Generated: ${generatedAt}`, pageWidth / 2, pageHeight - 5, {
          align: "center",
        });
        pdf.text(
          `Page ${page} of ${totalPages}`,
          pageWidth - margin,
          pageHeight - 5,
          {
            align: "right",
          },
        );
      }

      pdf.save("Market-Search-History.pdf");
    } catch (error) {
      console.error("Export search history PDF error:", error);
      setError("Failed to export search history PDF.");
    }
  };

  const handleLoadLastSearch = async () => {
    try {
      setLoading(true);
      setError("");
      setAccessMessage("");

      const response = await axios.get(
        `${API_BASE_URL}/market-analysis/last-search`,
        {
          withCredentials: true,
        },
      );

      if (!response.data?.success) {
        setError(response.data?.message || "No last search found.");
        return;
      }

      const lastSearchData = response.data.data;

      setAnalysis(lastSearchData);
      setMarketTrendPoints(lastSearchData?.marketTrend?.points || []);
      setAiSuggestion(lastSearchData?.aiSuggestion || null);

      if (lastSearchData?.wallet) {
        setWalletBalance(Number(lastSearchData.wallet.balanceAmount || 0));
      }

      if (lastSearchData?.searchKeyword) {
        setSearchKeyword(lastSearchData.searchKeyword);
      }

      if (lastSearchData?.locationName) {
        setCountrySearch(lastSearchData.locationName);
        setSelectedCountryName(lastSearchData.locationName);
      }

      localStorage.setItem(
        LAST_SEARCH_STORAGE_KEY,
        JSON.stringify({
          analysis: lastSearchData,
          marketTrendPoints: lastSearchData?.marketTrend?.points || [],
          aiSuggestion: lastSearchData?.aiSuggestion || null,
          savedAt: new Date().toISOString(),
        }),
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load last search.",
      );
    } finally {
      setLoading(false);
    }
  };

  const REPORT_URL = "https://lyrazone.com.my/market-price-analysis";

  const getReportDateTime = () => {
    return new Intl.DateTimeFormat("en-MY", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(new Date());
  };

  const handleExportSearchPdf = async () => {
    try {
      if (!pdfReportRef.current || !analysis) {
        setError("No search result available to export.");
        return;
      }

      const html2canvas = (await import("html2canvas")).default;
      const jsPDFModule = await import("jspdf");
      const jsPDF = jsPDFModule.default;

      const generatedAt = getReportDateTime();

      const canvas = await html2canvas(pdfReportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#f8f6f0",
        windowWidth: 1200,
        ignoreElements: (element) => {
          return element.classList?.contains("no-print");
        },
      });

      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const margin = 8;
      const headerHeight = 10;
      const footerHeight = 9;

      const contentX = margin;
      const contentY = margin + headerHeight;
      const contentWidth = pageWidth - margin * 2;
      const contentHeight =
        pageHeight - margin * 2 - headerHeight - footerHeight;

      const pxPerMm = canvas.width / contentWidth;
      const sliceHeightPx = Math.floor(contentHeight * pxPerMm);

      let sourceY = 0;
      let pageNo = 1;

      while (sourceY < canvas.height) {
        if (pageNo > 1) {
          pdf.addPage();
        }

        const currentSliceHeight = Math.min(
          sliceHeightPx,
          canvas.height - sourceY,
        );

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = currentSliceHeight;

        const ctx = pageCanvas.getContext("2d");

        if (ctx) {
          ctx.fillStyle = "#f8f6f0";
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

          ctx.drawImage(
            canvas,
            0,
            sourceY,
            canvas.width,
            currentSliceHeight,
            0,
            0,
            canvas.width,
            currentSliceHeight,
          );
        }

        const pageImgData = pageCanvas.toDataURL("image/png");
        const pageImgHeight = currentSliceHeight / pxPerMm;

        pdf.addImage(
          pageImgData,
          "PNG",
          contentX,
          contentY,
          contentWidth,
          pageImgHeight,
        );

        sourceY += currentSliceHeight;
        pageNo += 1;
      }

      const totalPages = pdf.getNumberOfPages();

      for (let page = 1; page <= totalPages; page += 1) {
        pdf.setPage(page);

        // Header background
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, contentY - 2, "F");

        // Header
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8.5);
        pdf.setTextColor(25, 25, 25);
        pdf.text(
          "LyraZone Solutions | Market Price Analysis Report",
          margin,
          7,
        );

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        pdf.setTextColor(90, 90, 90);
        pdf.text(`Generated: ${generatedAt}`, margin, 11);

        pdf.text(REPORT_URL, pageWidth - margin, 11, {
          align: "right",
        });

        pdf.setDrawColor(209, 157, 40);
        pdf.setLineWidth(0.2);
        pdf.line(margin, 14, pageWidth - margin, 14);

        // Footer background
        pdf.setFillColor(255, 255, 255);
        pdf.rect(
          0,
          pageHeight - footerHeight - 2,
          pageWidth,
          footerHeight + 2,
          "F",
        );

        pdf.setDrawColor(229, 220, 199);
        pdf.setLineWidth(0.2);
        pdf.line(
          margin,
          pageHeight - footerHeight - 1,
          pageWidth - margin,
          pageHeight - footerHeight - 1,
        );

        // Footer
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        pdf.setTextColor(90, 90, 90);

        pdf.text(REPORT_URL, margin, pageHeight - 5);

        pdf.text(`Generated: ${generatedAt}`, pageWidth / 2, pageHeight - 5, {
          align: "center",
        });

        pdf.text(
          `Page ${page} of ${totalPages}`,
          pageWidth - margin,
          pageHeight - 5,
          {
            align: "right",
          },
        );
      }

      const fileName = `Market-Analysis-${analysis.searchKeyword || "Search"}-${
        analysis.locationName || selectedCountryName || "Market"
      }.pdf`
        .replace(/\s+/g, "-")
        .replace(/[^\w.-]/g, "");

      pdf.save(fileName);
    } catch (error) {
      console.error("Export PDF error:", error);
      setError("Failed to export search result PDF.");
    }
  };

  const handleNewSearch = () => {
    setAnalysis(null);
    setSearchKeyword("");
    setMarketTrendPoints([]);
    setAiSuggestion(null);
    setError("");
    setAccessMessage("");
    localStorage.removeItem(LAST_SEARCH_STORAGE_KEY);
  };

  const handleWalletTopUp = () => {
    setWalletModalOpen(false);
    navigate("/market-analysis-pricing");
  };

  const fetchWalletHistory = async () => {
    const latestStoredUser = getStoredSubscriberUser();

    setStoredUser(latestStoredUser);

    if (!latestStoredUser) {
      showModal({
        type: "warning",
        title: "Warning",
        message: "You are not logged in, please login first.",
      });
      return;
    }

    try {
      setWalletHistoryLoading(true);
      setWalletHistoryError("");

      const response = await axios.get(
        `${API_BASE_URL}/market-analysis/wallet/history`,
        {
          withCredentials: true,
        },
      );

      if (response.data?.success) {
        setWalletHistory(response.data.data);

        if (response.data.data?.wallet) {
          setWalletBalance(
            Number(response.data.data.wallet.balanceAmount || 0),
          );
        }
      } else {
        setWalletHistoryError(
          response.data?.message || "Unable to fetch wallet history.",
        );
      }
    } catch (error: any) {
      setWalletHistoryError(
        error?.response?.data?.message ||
          error.message ||
          "Unable to fetch wallet history.",
      );
    } finally {
      setWalletHistoryLoading(false);
    }
  };

  const handleOpenWalletModal = async () => {
    setWalletModalOpen(true);
    setWalletActiveTab("transactions");
    await fetchWalletHistory();
  };

  const fetchSubscriberWallet = async () => {
    const latestStoredUser = getStoredSubscriberUser();

    setStoredUser(latestStoredUser);

    if (!latestStoredUser) {
      setWalletBalance(null);
      return;
    }

    try {
      setWalletLoading(true);

      const response = await axios.get(
        `${API_BASE_URL}/market-analysis/wallet`,
        {
          withCredentials: true,
        },
      );

      if (response.data?.success) {
        setWalletBalance(Number(response.data.data?.balanceAmount || 0));
      }
    } catch (error) {
      console.error("Fetch subscriber wallet error:", error);
      setWalletBalance(null);
    } finally {
      setWalletLoading(false);
    }
  };

  const getUrlHostName = (url?: string | null) => {
    try {
      if (!url) return "";

      return new URL(url).hostname.replace(/^www\./, "");
    } catch {
      return "";
    }
  };

  const getMarketResultSourceLabel = (row: {
    source?: string | null;
    domain?: string | null;
    url?: string | null;
    sellerName?: string | null;
    seller_name?: string | null;
  }) => {
    return (
      row.source ||
      row.domain ||
      getUrlHostName(row.url) ||
      row.sellerName ||
      row.seller_name ||
      "-"
    );
  };

  const getMarketResultUrl = (row: {
    url?: string | null;
    domain?: string | null;
  }) => {
    const rawUrl = String(row.url || "").trim();

    if (!rawUrl) return "";

    if (/^https?:\/\//i.test(rawUrl)) {
      return rawUrl;
    }

    return `https://${rawUrl}`;
  };

  const handleOpenMarketResult = (row: NormalizedMarketResult) => {
    const productUrl = getMarketResultUrl(row);

    if (!productUrl) {
      return;
    }

    window.open(productUrl, "_blank", "noopener,noreferrer");
  };

  const normalizeMarketResults = (
    results: MarketResult[] = [],
  ): NormalizedMarketResult[] => {
    return results.map((item) => {
      const sourceLabel = getMarketResultSourceLabel({
        source: item.source,
        domain: item.domain,
        url: item.url,
        sellerName: item.sellerName,
        seller_name: item.seller_name,
      });

      return {
        id: item.id,
        title: item.title || "-",
        sellerName: item.sellerName || item.seller_name || "-",
        domain: item.domain || getUrlHostName(item.url) || sourceLabel || "-",
        source: sourceLabel,
        url: String(
          item.url ||
            (item as any).link ||
            (item as any).productUrl ||
            (item as any).product_url ||
            (item as any).sourceUrl ||
            (item as any).source_url ||
            "",
        ).trim(),
        priceValue:
          item.priceValue !== undefined && item.priceValue !== null
            ? Number(item.priceValue)
            : item.price_value !== undefined && item.price_value !== null
              ? Number(item.price_value)
              : null,
        originalPriceValue:
          item.originalPriceValue !== undefined &&
          item.originalPriceValue !== null
            ? Number(item.originalPriceValue)
            : item.original_price_value !== undefined &&
                item.original_price_value !== null
              ? Number(item.original_price_value)
              : null,

        originalCurrency:
          item.originalCurrency ||
          item.original_currency ||
          item.currency ||
          "",

        conversionRate:
          item.conversionRate !== undefined && item.conversionRate !== null
            ? Number(item.conversionRate)
            : item.conversion_rate !== undefined &&
                item.conversion_rate !== null
              ? Number(item.conversion_rate)
              : null,
        currency: item.currency || item.currencyCode || "",
        currencyCode: item.currencyCode || item.currency || "",
        rating:
          item.rating !== undefined && item.rating !== null
            ? Number(item.rating)
            : null,
        reviewsCount:
          item.reviewsCount !== undefined && item.reviewsCount !== null
            ? Number(item.reviewsCount)
            : item.reviews_count !== undefined && item.reviews_count !== null
              ? Number(item.reviews_count)
              : null,
        rankPosition:
          item.rankPosition !== undefined && item.rankPosition !== null
            ? Number(item.rankPosition)
            : item.rank_position !== undefined && item.rank_position !== null
              ? Number(item.rank_position)
              : null,
        matchScore:
          item.matchScore !== undefined && item.matchScore !== null
            ? Number(item.matchScore)
            : item.match_score !== undefined && item.match_score !== null
              ? Number(item.match_score)
              : 0,
        matchType: item.matchType || item.match_type || "Unmatched",
      };
    });
  };

  const marketResultRows = useMemo(() => {
    return normalizeMarketResults(analysis?.results || []);
  }, [analysis]);

  const normalizeCurrencyCode = (
    currencyCode?: string | null,
    fallback = "",
  ) => {
    return String(currencyCode || fallback)
      .trim()
      .toUpperCase();
  };

  const marketCurrencyCode = useMemo(() => {
    if (analysis?.currencyCode) {
      return normalizeCurrencyCode(analysis.currencyCode, "MYR");
    }

    const rowCurrency = marketResultRows.find((row) => row.currency)?.currency;

    return normalizeCurrencyCode(rowCurrency, "MYR");
  }, [analysis?.currencyCode, marketResultRows]);

  const hasPriceFluctuationData = useMemo(() => {
    return (
      marketTrendPoints.filter((item) => Number(item.average_price || 0) > 0)
        .length > 1
    );
  }, [marketTrendPoints]);

  const getMarketRowCurrencyCode = (row?: NormalizedMarketResult) => {
    const rowCurrency = normalizeCurrencyCode(row?.currency || "");

    if (!rowCurrency) {
      return marketCurrencyCode;
    }

    if (rowCurrency === "MYR" && marketCurrencyCode !== "MYR") {
      return marketCurrencyCode;
    }

    return rowCurrency;
  };

  const formatMarketResultCurrency = (
    value?: number | string | null,
    currencyCode?: string | null,
  ) => {
    const amount = Number(value || 0);

    return `${normalizeCurrencyCode(currencyCode)} ${amount.toLocaleString(
      "en-MY",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    )}`;
  };

  const fetchMarketTrend = async ({
    keyword,
    locationName,
    rangeDays,
  }: {
    keyword: string;
    locationName?: string;
    rangeDays: 30 | 90;
  }) => {
    try {
      setTrendLoading(true);

      const response = await axios.get(
        `${API_BASE_URL}/market-analysis/trend`,
        {
          params: {
            searchKeyword: keyword,
            locationName: locationName || "Malaysia",
            rangeDays,
          },
          withCredentials: true,
        },
      );

      if (response.data?.success) {
        setMarketTrendPoints(response.data.data?.points || []);
        setAiSuggestion(response.data.data?.aiSuggestion || null);
      }
    } catch (error) {
      console.error("Fetch market trend error:", error);
    } finally {
      setTrendLoading(false);
    }
  };

  const getTopupPaymentMethodLabel = (item: WalletTopup) => {
    if (item.display_method) return item.display_method;

    if (item.payment_provider === "WebsiteOffer") {
      return item.payment_method || "Website Offer";
    }

    if (item.payment_provider === "Stripe") return "Card Payment";
    if (item.payment_provider === "SecurePay") return "Online Banking";

    if (item.payment_method === "Stripe") return "Card Payment";
    if (item.payment_method === "Online Banking") return "Online Banking";

    return item.payment_method || "-";
  };

  const marketResultColumns: Column[] = useMemo(
    () => [
      {
        key: "rowNo",
        label: "#",
        filterable: false,
        render: (_row, rowIndex) => rowIndex + 1,
      },
      {
        key: "title",
        label: "Market Product",
      },
      {
        key: "sellerName",
        label: "Seller",
      },
      {
        key: "priceValue",
        label: "Price",
        render: (row: NormalizedMarketResult) => {
          const originalCurrency = normalizeCurrencyCode(
            row.originalCurrency || row.currency || marketCurrencyCode,
            marketCurrencyCode,
          );

          const compareCurrency = normalizeCurrencyCode(
            row.currency || marketCurrencyCode,
            marketCurrencyCode,
          );

          const originalPrice = row.originalPriceValue ?? row.priceValue;
          const convertedPrice = row.priceValue;

          const showConverted =
            originalCurrency &&
            compareCurrency &&
            originalCurrency !== compareCurrency &&
            originalPrice !== null &&
            originalPrice !== undefined &&
            convertedPrice !== null &&
            convertedPrice !== undefined;

          return (
            <div className="market-analysis-price-cell">
              <strong>
                {formatMarketResultCurrency(originalPrice, originalCurrency)}
              </strong>

              {showConverted && (
                <small>
                  ≈{" "}
                  {formatMarketResultCurrency(convertedPrice, compareCurrency)}
                </small>
              )}
            </div>
          );
        },
      },
      {
        key: "matchType",
        label: "Match",
        render: (row: NormalizedMarketResult) => (
          <span
            className={`market-analysis-badge ${String(
              row.matchType || "Unmatched",
            ).toLowerCase()}`}
          >
            {row.matchType || "Unmatched"}
          </span>
        ),
      },
      {
        key: "matchScore",
        label: "Score",
        render: (row: NormalizedMarketResult) => `${row.matchScore || 0}%`,
      },
      {
        key: "rating",
        label: "Rating",
        render: (row: NormalizedMarketResult) =>
          row.rating ? `${row.rating} (${row.reviewsCount || 0})` : "-",
      },
      // {
      //   key: "domain",
      //   label: "Source",
      //   render: (row: NormalizedMarketResult) =>
      //     row.url ? (
      //       <a
      //         href={row.url}
      //         target="_blank"
      //         rel="noreferrer"
      //         className="market-analysis-source-link"
      //         onClick={(event) => event.stopPropagation()}
      //       >
      //         View
      //       </a>
      //     ) : (
      //       row.domain || "-"
      //     ),
      // },
      {
        key: "source",
        label: "Source",
        render: (row: NormalizedMarketResult) => {
          const sourceLabel = getMarketResultSourceLabel(row);

          if (row.url && sourceLabel !== "-") {
            return (
              <a
                href={row.url}
                target="_blank"
                rel="noreferrer"
                className="market-analysis-source-link"
                onClick={(event) => event.stopPropagation()}
              >
                {sourceLabel}
              </a>
            );
          }

          return sourceLabel;
        },
      },
    ],
    [marketCurrencyCode],
  );

  const filteredRegions = useMemo(() => {
    const search = countrySearch.toLowerCase().trim();

    if (!search) return regions;

    return regions.filter((region) => {
      return (
        region.region_name?.toLowerCase().includes(search) ||
        region.country_code?.toLowerCase().includes(search) ||
        region.region_code?.toLowerCase().includes(search)
      );
    });
  }, [regions, countrySearch]);

  const handleAnalyze = async () => {
    if (!storedUser) {
      showModal({
        type: "warning",
        title: "Warning",
        message: "You are not logged in, please login first.",
      });
      return;
    }

    if (!searchKeyword.trim()) {
      setError("Please enter a product keyword.");
      return;
    }

    if (!selectedCountryName) {
      setError("Please select a country from the dropdown.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setAccessMessage("");
      setAnalysis(null);

      const response = await axios.post(
        `${API_BASE_URL}/market-analysis/analyze`,
        {
          searchKeyword: searchKeyword.trim(),
          countryName: selectedCountryName,
          countryCode: selectedCountryCode || undefined,
        },
        {
          withCredentials: true,
        },
      );

      if (response.data?.success) {
        const analysisData = response.data.data;

        setAnalysis(analysisData);

        localStorage.setItem(
          LAST_SEARCH_STORAGE_KEY,
          JSON.stringify({
            analysis: analysisData,
            marketTrendPoints: analysisData?.marketTrend?.points || [],
            aiSuggestion: analysisData?.aiSuggestion || null,
            savedAt: new Date().toISOString(),
          }),
        );

        setMarketTrendPoints(analysisData?.marketTrend?.points || []);
        setAiSuggestion(analysisData?.aiSuggestion || null);
        setTrendRange(30);

        if (analysisData?.wallet) {
          setWalletBalance(Number(analysisData.wallet.balanceAmount || 0));
        }
      } else {
        setError(response.data?.message || "Market analysis failed.");
      }
    } catch (err: any) {
      const responseData = err?.response?.data;

      if (err?.response?.status === 401 || responseData?.requiresAuth) {
        showModal({
          type: "warning",
          title: "Warning",
          message: "You are not logged in, please login first.",
        });
        return;
      }

      if (err?.response?.status === 402 || responseData?.requiresAccess) {
        setAccessMessage(
          responseData?.message ||
            "Please get access for more Market Analysis, click here.",
        );
        return;
      }

      setError(
        responseData?.error ||
          responseData?.message ||
          err.message ||
          "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTrendRangeChange = async (rangeDays: 30 | 90) => {
    setTrendRange(rangeDays);

    if (!analysis?.searchKeyword) return;

    await fetchMarketTrend({
      keyword: analysis.searchKeyword,
      locationName: analysis.locationName || "Malaysia",
      rangeDays,
    });
  };

  return (
    <>
      <div className="market-analysis-page">
        <section className="market-analysis-hero">
          <div className="market-analysis-hero-inner">
            <div className="market-analysis-hero-content">
              <button
                type="button"
                className="market-analysis-eyebrow market-analysis-tour-guide-btn"
                onClick={startMarketAnalysisTour}
              >
                Tour Guide
              </button>

              <h1>Market Price Analysis</h1>

              <p>
                Search {selectedCountryName || "selected country"} market
                pricing by product keyword and compare current online price
                ranges before making your purchase or pricing decision.
              </p>
            </div>

            {storedUser && (
              <div className="market-analysis-wallet-area">
                <button
                  type="button"
                  className="market-wallet-topup-btn market-analysis-hero-topup-btn"
                  onClick={handleWalletTopUp}
                  data-tour="top-up-balance"
                >
                  Top Up Balance
                </button>

                <button
                  type="button"
                  className="market-analysis-wallet-card"
                  onClick={handleOpenWalletModal}
                  data-tour="wallet-badge"
                >
                  <span>Available Balance</span>

                  <strong>
                    {walletLoading
                      ? "Loading..."
                      : formatLocalCurrencyOnly(walletBalance || 0, {
                          showCode: true,
                        })}
                  </strong>

                  <small>Market Analysis Wallet</small>
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="market-analysis-content">
          <div className="market-analysis-search-card">
            <div className="market-analysis-search-title">
              <h2>Search Market Price</h2>
              <p>
                Enter a product name, model, barcode, or keyword to check
                available {selectedCountryName || "selected country"} market
                prices.
              </p>
            </div>

            <div className="market-analysis-search-grid market-analysis-search-grid-with-country">
              <div className="market-analysis-field">
                <label>Select Market Country</label>

                <div
                  className="market-analysis-country-dropdown-wrapper"
                  ref={countryDropdownRef}
                  data-tour="market-country"
                >
                  <input
                    value={countrySearch}
                    onChange={(e) => {
                      setCountrySearch(e.target.value);
                      setSelectedCountryName("");
                      setSelectedCountryCode("");
                      setShowCountryDropdown(true);
                      setAccessMessage("");
                      setError("");
                    }}
                    onFocus={() => setShowCountryDropdown(true)}
                    placeholder="Select country"
                  />

                  <button
                    type="button"
                    className={`market-analysis-country-arrow ${
                      showCountryDropdown ? "open" : ""
                    }`}
                    onClick={() => setShowCountryDropdown((prev) => !prev)}
                    aria-label="Toggle country dropdown"
                  >
                    ▾
                  </button>

                  {showCountryDropdown && (
                    <div className="market-analysis-country-dropdown">
                      {filteredRegions.length > 0 ? (
                        filteredRegions.map((region) => (
                          <button
                            type="button"
                            key={region.id}
                            className="market-analysis-country-option"
                            onClick={() => {
                              setCountrySearch(region.region_name);
                              setSelectedCountryName(region.region_name);
                              setSelectedCountryCode(region.country_code || "");
                              setShowCountryDropdown(false);
                              setAccessMessage("");
                              setError("");
                            }}
                          >
                            <span>{region.region_name}</span>
                            {region.country_code && (
                              <small>{region.country_code}</small>
                            )}
                          </button>
                        ))
                      ) : (
                        <div className="market-analysis-country-empty">
                          No country found.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="market-analysis-field">
                <label>Search Product Keyword</label>

                <input
                  value={searchKeyword}
                  onChange={(e) => {
                    setSearchKeyword(e.target.value);
                    setAccessMessage("");
                    setError("");
                  }}
                  placeholder={`Example: Brand Model of any product of any industry of ${selectedCountryName || "selected country"}`}
                  data-tour="search-keyword"
                />

                {accessMessage && (
                  <div className="market-analysis-access-message">
                    Please get access for more Market Analysis,{" "}
                    <button
                      type="button"
                      className="market-analysis-hero-topup-btn"
                      onClick={() => navigate("/market-analysis-pricing")}
                    >
                      Add Funds
                    </button>
                  </div>
                )}
              </div>

              <div className="market-analysis-search-actions">
                <button
                  className="market-analysis-btn"
                  onClick={handleAnalyze}
                  disabled={loading}
                  data-tour="analyze-market"
                >
                  {loading ? "Analyzing..." : "Analyze Market Price"}
                </button>

                {storedUser && (
                  <button
                    type="button"
                    className="market-analysis-last-search-btn"
                    onClick={handleLoadLastSearch}
                    disabled={loading}
                    data-tour="last-search"
                  >
                    Last Search
                  </button>
                )}
              </div>
            </div>

            {error && <div className="market-analysis-error">{error}</div>}
          </div>

          {analysis && (
            <div className="market-analysis-result-actions no-print">
              <button
                type="button"
                className="market-analysis-export-btn"
                onClick={handleExportSearchPdf}
              >
                Export Search
              </button>

              <button
                type="button"
                className="market-analysis-secondary-action-btn"
                onClick={handleOpenSearchHistory}
                disabled={loading}
              >
                Search History
              </button>

              <button
                type="button"
                className="market-analysis-secondary-action-btn"
                onClick={handleNewSearch}
              >
                New Search
              </button>
            </div>
          )}

          {analysis && (
            <>
              <div ref={exportRef} className="market-analysis-export-area">
                <div className="market-analysis-summary">
                  <div className="market-analysis-card">
                    <span>Lowest Market Price</span>
                    <strong>
                      {formatMarketResultCurrency(
                        analysis.lowestPrice,
                        marketCurrencyCode,
                      )}
                    </strong>
                    <small>Based on market results</small>
                  </div>

                  <div className="market-analysis-card">
                    <span>Average Market Price</span>
                    <strong>
                      {formatMarketResultCurrency(
                        analysis.averagePrice,
                        marketCurrencyCode,
                      )}
                    </strong>
                    <small>
                      {analysis.locationName || selectedCountryName} market
                      average
                    </small>
                  </div>

                  <div className="market-analysis-card">
                    <span>Highest Market Price</span>
                    <strong>
                      {formatMarketResultCurrency(
                        analysis.highestPrice,
                        marketCurrencyCode,
                      )}
                    </strong>
                    <small>Based on market results</small>
                  </div>

                  <div className="market-analysis-card">
                    <span>Results Found</span>
                    <strong>{analysis.resultCount}</strong>
                    <small>
                      {analysis.accessType === "Free"
                        ? "Free search used"
                        : `Charged ${formatLocalCurrencyOnly(analysis.chargedAmount, { showCode: true })}`}
                    </small>
                  </div>
                </div>

                <div className="market-analysis-summary-box">
                  <h3>Market Search Summary</h3>

                  <p>
                    <b>Search At:</b>{" "}
                    {analysis.created_at
                      ? formatDateToDMY(analysis.created_at)
                      : "-"}
                  </p>

                  <p>
                    <b>Market Country:</b>{" "}
                    {analysis.locationName || selectedCountryName}
                  </p>

                  <p>
                    <b>Search Keyword:</b> {analysis.searchKeyword}
                  </p>

                  {analysis.wallet && (
                    <p>
                      <b>Available Balance:</b>{" "}
                      {formatLocalCurrencyOnly(analysis.wallet.balanceAmount, {
                        showCode: true,
                      })}
                    </p>
                  )}
                </div>
                <div className="market-trend-section">
                  {hasPriceFluctuationData && (
                    <>
                      <div className="market-trend-header">
                        <div>
                          <h3>Price Fluctuation</h3>
                          <p>
                            Based on stored market search snapshots in the
                            LyraZone Database for this keyword and location.
                          </p>
                        </div>

                        <div className="market-trend-range-tabs">
                          <button
                            type="button"
                            className={trendRange === 30 ? "active" : ""}
                            onClick={() => handleTrendRangeChange(30)}
                          >
                            Last 30 Days
                          </button>

                          <button
                            type="button"
                            className={trendRange === 90 ? "active" : ""}
                            onClick={() => handleTrendRangeChange(90)}
                          >
                            3 Months
                          </button>
                        </div>
                      </div>

                      {trendLoading ? (
                        <div className="market-trend-empty">
                          Loading price fluctuation...
                        </div>
                      ) : (
                        <MarketPriceTrendChart
                          points={marketTrendPoints}
                          formatMarketResultCurrency={(value) =>
                            formatMarketResultCurrency(
                              value,
                              marketCurrencyCode,
                            )
                          }
                        />
                      )}
                    </>
                  )}

                  {aiSuggestion && (
                    <div className="market-ai-suggestion-grid">
                      <div className="market-ai-suggestion-card seller">
                        <span>AI Seller Suggestion</span>

                        <h4>
                          Suggested Price:{" "}
                          {aiSuggestion.seller.suggestedPrice
                            ? formatMarketResultCurrency(
                                aiSuggestion.seller.suggestedPrice,
                                marketCurrencyCode,
                              )
                            : "-"}
                        </h4>

                        <p>{aiSuggestion.seller.message}</p>
                      </div>

                      <div className="market-ai-suggestion-card buyer">
                        <span>AI Buyer Suggestion</span>

                        <h4>
                          Target Buy Price:{" "}
                          {aiSuggestion.buyer.targetPrice
                            ? formatMarketResultCurrency(
                                aiSuggestion.buyer.targetPrice,
                                marketCurrencyCode,
                              )
                            : "-"}
                        </h4>

                        <p>{aiSuggestion.buyer.message}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="market-analysis-results-section">
                  <div className="market-analysis-results-header">
                    <h3>Market Price Results</h3>
                    <span>{marketResultRows.length} Result(s)</span>
                  </div>

                  <div className="market-analysis-desktop-table">
                    <DataTable
                      columns={marketResultColumns}
                      data={marketResultRows}
                      getRowKey={(row, index) =>
                        row.id || `${row.title}-${row.sellerName}-${index}`
                      }
                      onRowSelect={handleOpenMarketResult}
                      getRowClassName={(row) =>
                        getMarketResultUrl(row)
                          ? "market-analysis-clickable-row"
                          : ""
                      }
                    />
                  </div>

                  <div className="market-analysis-mobile-cards">
                    {marketResultRows.length === 0 ? (
                      <div className="market-analysis-mobile-empty">
                        No market price results found.
                      </div>
                    ) : (
                      marketResultRows.map((row, index) => (
                        <div
                          className="market-analysis-mobile-card"
                          key={
                            row.id || `${row.title}-${row.sellerName}-${index}`
                          }
                        >
                          <div className="market-analysis-mobile-status">
                            <span
                              className={`market-analysis-mobile-status-badge ${String(
                                row.matchType || "Unmatched",
                              ).toLowerCase()}`}
                            >
                              {row.matchType || "Unmatched"}
                            </span>
                          </div>

                          <div className="market-analysis-mobile-field">
                            <span>Market Product</span>
                            <strong>{row.title || "-"}</strong>
                          </div>

                          <div className="market-analysis-mobile-field">
                            <span>Seller</span>
                            <strong>{row.sellerName || "-"}</strong>
                          </div>

                          <div className="market-analysis-mobile-info-row">
                            <div className="market-analysis-mobile-info-item">
                              <span>Price</span>
                              <strong>
                                {formatMarketResultCurrency(
                                  row.priceValue,
                                  getMarketRowCurrencyCode(row),
                                )}
                              </strong>
                            </div>

                            <div className="market-analysis-mobile-info-item">
                              <span>Score</span>
                              <strong>{row.matchScore || 0}%</strong>
                            </div>

                            <div className="market-analysis-mobile-info-item">
                              <span>Rating</span>
                              <strong>
                                {row.rating
                                  ? `${row.rating} (${row.reviewsCount || 0})`
                                  : "-"}
                              </strong>
                            </div>
                          </div>

                          {row.url ? (
                            <a
                              href={row.url}
                              target="_blank"
                              rel="noreferrer"
                              className="market-analysis-mobile-view-btn"
                              onClick={(event) => event.stopPropagation()}
                            >
                              View Source
                            </a>
                          ) : (
                            <button
                              type="button"
                              className="market-analysis-mobile-view-btn disabled"
                              disabled
                            >
                              No Source
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  {analysis && (
                    <div className="market-analysis-result-actions bottom no-print">
                      <button
                        type="button"
                        className="market-analysis-export-btn"
                        onClick={handleExportSearchPdf}
                      >
                        Export Search
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </section>
        {walletModalOpen && (
          <div
            className="market-wallet-modal-overlay"
            onClick={() => setWalletModalOpen(false)}
          >
            <div
              className="market-wallet-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="market-wallet-modal-header">
                <div className="market-wallet-modal-balance">
                  <span>Market Analysis Wallet</span>
                  <h3>
                    {formatLocalCurrencyOnly(
                      walletHistory?.wallet?.balanceAmount ??
                        walletBalance ??
                        0,
                      { showCode: true },
                    )}
                  </h3>
                  <p>Available Balance</p>
                </div>

                <div className="market-wallet-modal-actions">
                  <button
                    type="button"
                    className="market-wallet-topup-btn"
                    onClick={handleWalletTopUp}
                  >
                    Top Up Balance
                  </button>

                  <button
                    type="button"
                    className="market-wallet-modal-close"
                    onClick={() => setWalletModalOpen(false)}
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="market-wallet-tabs">
                <button
                  type="button"
                  className={walletActiveTab === "transactions" ? "active" : ""}
                  onClick={() => setWalletActiveTab("transactions")}
                >
                  Transaction History
                </button>

                <button
                  type="button"
                  className={walletActiveTab === "topups" ? "active" : ""}
                  onClick={() => setWalletActiveTab("topups")}
                >
                  Top-up History
                </button>
              </div>

              <div className="market-wallet-modal-body">
                {walletHistoryLoading ? (
                  <div className="market-wallet-empty">
                    Loading wallet history...
                  </div>
                ) : walletHistoryError ? (
                  <div className="market-wallet-error">
                    {walletHistoryError}
                  </div>
                ) : walletActiveTab === "transactions" ? (
                  walletHistory?.transactions?.length ? (
                    <div className="market-wallet-list">
                      {walletHistory.transactions.map((item) => (
                        <div className="market-wallet-row" key={item.id}>
                          <div>
                            <strong>{item.transaction_type}</strong>

                            {item.search_keyword ? (
                              <>
                                <span>Search: {item.search_keyword}</span>
                                <small>
                                  {item.note || "-"} •{" "}
                                  {formatDateToDMY(item.created_at)}
                                </small>
                              </>
                            ) : (
                              <>
                                <span>
                                  {item.note || item.reference_type || "-"}
                                </span>
                                <small>
                                  {formatDateToDMY(item.created_at)}
                                </small>
                              </>
                            )}
                          </div>

                          <b
                            className={
                              item.transaction_type === "Debit"
                                ? "debit"
                                : item.transaction_type === "FreeSearch"
                                  ? "free"
                                  : "credit"
                            }
                          >
                            {item.transaction_type === "Debit" ? "-" : "+"}
                            {formatLocalCurrencyOnly(Number(item.amount || 0), {
                              showCode: true,
                            })}
                          </b>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="market-wallet-empty">
                      No transaction history found.
                    </div>
                  )
                ) : walletHistory?.topups?.length ? (
                  <div className="market-wallet-list">
                    {walletHistory.topups.map((item) => (
                      <div className="market-wallet-row" key={item.id}>
                        <div>
                          <strong>
                            {item.title || "Market Analysis Top-up"}
                          </strong>
                          <span>
                            Ref: {item.display_ref || item.payment_ref}
                          </span>
                          <span>
                            Method: {getTopupPaymentMethodLabel(item)}
                          </span>
                          <small>{formatDateToDMY(item.created_at)}</small>
                        </div>

                        <div className="market-wallet-topup-right">
                          <b>
                            {formatLocalCurrencyOnly(Number(item.amount || 0), {
                              showCode: true,
                            })}
                          </b>

                          <span className="market-wallet-payment-method">
                            {getTopupPaymentMethodLabel(item)}
                          </span>

                          <em
                            className={String(item.status || "").toLowerCase()}
                          >
                            {item.status}
                          </em>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="market-wallet-empty">
                    No top-up history found.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* HISTORY MODAL */}
      {showSearchHistoryModal && (
        <div className="market-history-modal-overlay">
          <div className="market-history-modal">
            <div className="market-history-modal-header">
              <div>
                <h3>Search History</h3>
                <p>
                  Latest searches are shown without any additional wallet
                  charge.
                </p>
              </div>

              <button
                type="button"
                className="market-history-modal-close"
                onClick={() => setShowSearchHistoryModal(false)}
              >
                ×
              </button>
            </div>

            <div className="market-history-modal-tools">
              <input
                type="text"
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                placeholder="Search keyword or country"
                onKeyDown={(e) => {
                  if (e.key === "Enter") fetchSearchHistory({ reset: true });
                }}
              />

              <input
                type="date"
                value={historyDateFrom}
                onChange={(e) => setHistoryDateFrom(e.target.value)}
              />

              <input
                type="date"
                value={historyDateTo}
                onChange={(e) => setHistoryDateTo(e.target.value)}
              />

              <button
                type="button"
                className="market-history-filter-btn"
                onClick={() => fetchSearchHistory({ reset: true })}
                disabled={searchHistoryLoading}
              >
                Apply
              </button>

              <button
                type="button"
                className="market-history-clear-btn"
                onClick={handleClearHistoryFilters}
                disabled={searchHistoryLoading}
              >
                Clear
              </button>

              <button
                type="button"
                className="market-history-export-btn"
                onClick={handleExportSearchHistoryPdf}
                disabled={searchHistoryLoading}
              >
                Export
              </button>
            </div>

            <div className="market-history-modal-body">
              {searchHistoryLoading && !searchHistoryRows.length ? (
                <div className="market-history-empty">
                  Loading search history...
                </div>
              ) : searchHistoryRows.length ? (
                searchHistoryRows.map((item) => (
                  <div className="market-history-card" key={item.runId}>
                    <div className="market-history-card-left">
                      <strong>{item.searchKeyword}</strong>

                      <span>Market: {item.locationName}</span>

                      <small>
                        Search At:{" "}
                        {item.created_at
                          ? formatDateToDMY(item.created_at)
                          : "-"}
                      </small>
                    </div>

                    <div className="market-history-card-mid">
                      <div className="market-history-price-grid">
                        <span>
                          Lowest
                          <b>
                            {formatMarketResultCurrency(
                              item.lowestPrice,
                              item.currencyCode,
                            )}
                          </b>
                        </span>

                        <span>
                          Average
                          <b>
                            {formatMarketResultCurrency(
                              item.averagePrice,
                              item.currencyCode,
                            )}
                          </b>
                        </span>

                        <span>
                          Highest
                          <b>
                            {formatMarketResultCurrency(
                              item.highestPrice,
                              item.currencyCode,
                            )}
                          </b>
                        </span>
                      </div>

                      <div className="market-history-meta-grid">
                        <span>
                          Results: <b>{item.resultCount}</b>
                        </span>

                        <span>
                          Charged:{" "}
                          <b>
                            {formatLocalCurrencyOnly(item.chargedAmount || 0, {
                              showCode: true,
                            })}
                          </b>
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="market-history-view-btn"
                      onClick={() => handleLoadSearchHistoryRecord(item.runId)}
                    >
                      View
                    </button>
                  </div>
                ))
              ) : (
                <div className="market-history-empty">
                  No search history found.
                </div>
              )}
            </div>

            <div className="market-history-modal-footer">
              <span>
                Showing {searchHistoryRows.length} of {searchHistoryTotal}
              </span>

              <div>
                {searchHistoryRows.length > SEARCH_HISTORY_LIMIT && (
                  <button
                    type="button"
                    className="market-history-show-less-btn"
                    onClick={handleShowLessSearchHistory}
                    disabled={searchHistoryLoading}
                  >
                    Show Less
                  </button>
                )}

                {searchHistoryHasMore && (
                  <button
                    type="button"
                    className="market-history-show-more-btn"
                    onClick={handleShowMoreSearchHistory}
                    disabled={searchHistoryLoading}
                  >
                    Show More
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* HIDDEN PDF EXPORT */}

      {analysis && (
        <div className="market-analysis-pdf-render-zone">
          <div ref={pdfReportRef} className="market-analysis-pdf-report">
            <section className="market-analysis-pdf-hero">
              <span className="market-analysis-pdf-eyebrow">
                LyraZone Solution
              </span>

              <h1>Market Price Analysis</h1>

              <p>
                Search{" "}
                {analysis.locationName ||
                  selectedCountryName ||
                  "selected country"}{" "}
                market pricing by product keyword and compare current online
                price ranges before making your purchase or pricing decision.
              </p>
            </section>

            <div className="market-analysis-pdf-content">
              <div className="market-analysis-summary">
                <div className="market-analysis-card">
                  <span>Lowest Market Price</span>
                  <strong>
                    {formatMarketResultCurrency(
                      analysis.lowestPrice,
                      marketCurrencyCode,
                    )}
                  </strong>
                  <small>Based on market results</small>
                </div>

                <div className="market-analysis-card">
                  <span>Average Market Price</span>
                  <strong>
                    {formatMarketResultCurrency(
                      analysis.averagePrice,
                      marketCurrencyCode,
                    )}
                  </strong>
                  <small>
                    {analysis.locationName || selectedCountryName} market
                    average
                  </small>
                </div>

                <div className="market-analysis-card">
                  <span>Highest Market Price</span>
                  <strong>
                    {formatMarketResultCurrency(
                      analysis.highestPrice,
                      marketCurrencyCode,
                    )}
                  </strong>
                  <small>Based on market results</small>
                </div>

                <div className="market-analysis-card">
                  <span>Results Found</span>
                  <strong>{analysis.resultCount}</strong>
                  <small>
                    Charged{" "}
                    {formatLocalCurrencyOnly(analysis.chargedAmount || 0, {
                      showCode: true,
                    })}
                  </small>
                </div>
              </div>

              <div className="market-analysis-summary-box">
                <h3>Market Search Summary</h3>

                <p>
                  <b>Market Country:</b>{" "}
                  {analysis.locationName || selectedCountryName || "-"}
                </p>

                <p>
                  <b>Search Keyword:</b> {analysis.searchKeyword}
                </p>

                <p>
                  <b>Available Balance:</b>{" "}
                  {formatLocalCurrencyOnly(
                    analysis.wallet?.balanceAmount ?? walletBalance ?? 0,
                    { showCode: true },
                  )}
                </p>
              </div>

              {hasPriceFluctuationData && (
                <>
                  <div className="market-trend-section">
                    <div className="market-trend-header">
                      <div>
                        <h3>Price Fluctuation</h3>
                        <p>
                          Based on stored market search snapshots in the
                          LyraZone Database for this keyword and location.
                        </p>
                      </div>
                    </div>

                    <MarketPriceTrendChart
                      points={marketTrendPoints}
                      formatMarketResultCurrency={(value) =>
                        formatMarketResultCurrency(value, marketCurrencyCode)
                      }
                    />
                  </div>
                </>
              )}

              {aiSuggestion && (
                <div className="market-ai-suggestion-grid">
                  <div className="market-ai-suggestion-card seller">
                    <span>AI Seller Suggestion</span>

                    <h4>
                      Suggested Price:{" "}
                      {aiSuggestion.seller.suggestedPrice
                        ? formatMarketResultCurrency(
                            aiSuggestion.seller.suggestedPrice,
                            marketCurrencyCode,
                          )
                        : "-"}
                    </h4>

                    <p>{aiSuggestion.seller.message}</p>
                  </div>

                  <div className="market-ai-suggestion-card buyer">
                    <span>AI Buyer Suggestion</span>

                    <h4>
                      Target Buy Price:{" "}
                      {aiSuggestion.buyer.targetPrice
                        ? formatMarketResultCurrency(
                            aiSuggestion.buyer.targetPrice,
                            marketCurrencyCode,
                          )
                        : "-"}
                    </h4>

                    <p>{aiSuggestion.buyer.message}</p>
                  </div>
                </div>
              )}

              <div className="market-analysis-results-section">
                <div className="market-analysis-results-header">
                  <h3>Market Price Results</h3>
                  <span>{marketResultRows.length} Result(s)</span>
                </div>

                <DataTable
                  columns={marketResultColumns}
                  data={marketResultRows}
                  getRowKey={(row, index) =>
                    row.id || `${row.title}-${row.sellerName}-${index}`
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {historyExportRows.length > 0 && (
        <div className="market-history-pdf-render-zone">
          <div ref={historyExportRef} className="market-history-pdf-report">
            <section className="market-history-pdf-hero">
              <span>LyraZone Solution</span>
              <h1>Market Search History</h1>
              <p>
                Search history report for Market Price Analysis.
                {historyDateFrom || historyDateTo
                  ? ` Date range: ${historyDateFrom || "Start"} to ${
                      historyDateTo || "Today"
                    }.`
                  : " Latest available search records."}
              </p>
            </section>

            <div className="market-history-pdf-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Search At</th>
                    <th>Keyword</th>
                    <th>Market</th>
                    <th>Lowest Price</th>
                    <th>Average Price</th>
                    <th>Highest Price</th>
                    <th>Results</th>
                    <th>Charged</th>
                  </tr>
                </thead>

                <tbody>
                  {historyExportRows.map((item, index) => (
                    <tr key={item.runId}>
                      <td>{index + 1}</td>

                      <td>
                        {item.created_at
                          ? formatDateToDMY(item.created_at)
                          : "-"}
                      </td>

                      <td>{item.searchKeyword}</td>

                      <td>{item.locationName}</td>

                      <td>
                        {formatMarketResultCurrency(
                          item.lowestPrice,
                          item.currencyCode,
                        )}
                      </td>

                      <td>
                        {formatMarketResultCurrency(
                          item.averagePrice,
                          item.currencyCode,
                        )}
                      </td>

                      <td>
                        {formatMarketResultCurrency(
                          item.highestPrice,
                          item.currencyCode,
                        )}
                      </td>

                      <td>{item.resultCount}</td>

                      <td>
                        {formatLocalCurrencyOnly(item.chargedAmount || 0, {
                          showCode: true,
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketPriceAnalysis;
